import { useEffect, useState } from "react";
import { Navbar } from "../components/Navbar";
import {
  useGetUserQuery,
  useRequestTwoFactorAuthMutation,
  useSetupTwoFactorAuthMutation,
  useUpdateEmailMutation,
  useUpdateUsernameMutation,
  useGetOrganizationsQuery,
  useLogoutMutation,
} from "../generated/graphql";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp";
import QRCode from "react-qr-code";
import { useToast } from "../components/ui/use-toast";
import { Toaster } from "../components/ui/toaster";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { useAuth } from "../AuthProvider";
import { UserIcon, MailIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import RightNavbar from "../components/RightNavbar";

export function Settings() {
  const { toast } = useToast();
  const { setOrganizationId } = useAuth();
  const navigate = useNavigate();
  const auth = useAuth();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");

  const [
    { data: updateUsernameData, error: updateUsernameError },
    updateUsername,
  ] = useUpdateUsernameMutation();
  const [{ data: updateEmailData, error: updateEmailError }, updateEmail] =
    useUpdateEmailMutation();
  const [
    { data: requestTwoAuthData, error: requestTwoAuthError },
    requestTwoAuth,
  ] = useRequestTwoFactorAuthMutation();
  const [{ data: setupTwoAuthData, error: setupTwoAuthError }, setupTwoAuth] =
    useSetupTwoFactorAuthMutation();
  const [{ data: userData, error: userError }, refetchUser] = useGetUserQuery();
  const [{ data: organizationsData }] = useGetOrganizationsQuery();
  const organizations = organizationsData?.getOrganizations;

  const user = userData?.getUser;

  useEffect(() => {
    setUsername(user?.username ?? "");
    setEmail(user?.email ?? "");
  }, [user]);

  const [{ data: logoutData, error: logoutError }, logout] =
    useLogoutMutation();

  // on logout
  useEffect(() => {
    const logout = async () => {
      if (logoutData) {
        await auth.signout();
        toast({
          title: "Success",
          description: "Logged out successfully",
          variant: "default",
        });
      }
    };
    logout();
  }, [logoutData, navigate, auth, toast]);

  useEffect(() => {
    if (logoutError) {
      toast({
        title: "Error",
        description: logoutError.message,
        variant: "destructive",
      });
    }
  }, [logoutError, toast]);

  useEffect(() => {
    if (updateUsernameData) {
      toast({
        title: "Success",
        description: "Username updated successfully",
      });
      refetchUser();
    }

    if (updateUsernameError) {
      toast({
        title: "Error",
        description: updateUsernameError.message,
        variant: "destructive",
      });
    }

    if (updateEmailData) {
      toast({
        title: "Success",
        description: "Email updated successfully",
      });
      refetchUser();
    }

    if (updateEmailError) {
      toast({
        title: "Error",
        description: updateEmailError.message,
        variant: "destructive",
      });
    }

    if (userError) {
      toast({
        title: "Error",
        description: userError.message,
        variant: "destructive",
      });
    }
  }, [
    refetchUser,
    toast,
    updateEmailData,
    updateEmailError,
    updateUsernameData,
    updateUsernameError,
    userError,
  ]);

  useEffect(() => {
    if (setupTwoAuthData) {
      toast({
        title: "Success",
        description: "MFA setup successfully",
      });
    }

    if (setupTwoAuthError) {
      toast({
        title: "Error",
        description: setupTwoAuthError.message,
        variant: "destructive",
      });
    }

    if (requestTwoAuthError) {
      toast({
        title: "Error",
        description: requestTwoAuthError.message,
        variant: "destructive",
      });
    }
  }, [requestTwoAuthError, setupTwoAuthData, setupTwoAuthError, toast]);

  return (
    <div className="flex min-h-screen w-full">
      <div
        className="flex-grow overflow-x-auto"
        style={{ width: "calc(100% - 60px)" }}
      >
        <div className="flex flex-col w-full mr-0 sm:pr-[40px] lg:pr-[60px]">
          <Navbar />
          <main className="flex-1 p-4 md:p-8">
            <Card x-chunk="A card showing the total revenue in USD and the percentage difference from last month.">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-lg font-semibold flex items-center">
                  User
                </CardTitle>
                {user?.has2fa && (
                  <CardTitle className="text-sm font-medium text-green-600">
                    MFA enabled
                  </CardTitle>
                )}
              </CardHeader>
              <CardContent className="mt-4">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <UserIcon className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium">{user?.username}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MailIcon className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm font-medium">{user?.email}</p>
                  </div>
                </div>
                <br />
                <Button
                  variant="destructive"
                  onClick={async () => {
                    await logout({});
                  }}
                  style={{ marginRight: 10 }}
                >
                  Log Out
                </Button>
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" style={{ marginRight: 10 }}>
                      Edit User
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="sm:mr-[60px]">
                    <SheetHeader>
                      <SheetTitle>Edit User</SheetTitle>
                    </SheetHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="name" className="text-right">
                          Name
                        </Label>
                        <Input
                          id="name"
                          placeholder="Username"
                          value={username}
                          onChange={(e) => {
                            setUsername(e.target.value);
                          }}
                          className="col-span-3"
                        />
                      </div>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="email" className="text-right">
                          Email
                        </Label>
                        <Input
                          id="email"
                          placeholder="Email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                          }}
                          className="col-span-3"
                        />
                      </div>
                    </div>
                    <SheetFooter>
                      <SheetClose asChild>
                        <Button
                          type="submit"
                          onClick={async () => {
                            if (username !== user?.username) {
                              await updateUsername({ username });
                            }

                            if (email !== user?.email) {
                              await updateEmail({ email });
                            }
                          }}
                        >
                          Save changes
                        </Button>
                      </SheetClose>
                    </SheetFooter>
                  </SheetContent>
                </Sheet>

                {user?.has2fa !== true && (
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          await requestTwoAuth({});
                        }}
                      >
                        Setup MFA
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="sm:mr-[60px]">
                      <SheetHeader>
                        <SheetTitle>Setup MFA</SheetTitle>
                      </SheetHeader>
                      <div className="grid gap-4 py-4">
                        {requestTwoAuthData?.requestTwoFactorAuth && (
                          <QRCode
                            value={requestTwoAuthData.requestTwoFactorAuth}
                          />
                        )}
                        {/* <img src={requestTwoAuthData?.requestTwoFactorAuth} /> */}
                        <div className="grid grid-cols-4 items-center gap-4">
                          <InputOTP
                            maxLength={6}
                            pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                            onComplete={async (value) => {
                              await setupTwoAuth({ secret: value });
                            }}
                          >
                            <InputOTPGroup>
                              <InputOTPSlot index={0} />
                              <InputOTPSlot index={1} />
                              <InputOTPSlot index={2} />
                              <InputOTPSlot index={3} />
                              <InputOTPSlot index={4} />
                              <InputOTPSlot index={5} />
                            </InputOTPGroup>
                          </InputOTP>
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                )}
              </CardContent>
            </Card>

            <Card style={{ marginTop: 20 }}>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center">
                  Organizations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Permission</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizations?.map((org, idx) => (
                      <TableRow
                        key={idx}
                        onClick={() => {
                          setOrganizationId(org.id);
                          toast({
                            title: "Organization selected",
                            description: "You have selected " + org.name,
                            variant: "default",
                          });
                        }}
                      >
                        <TableCell>{org.name}</TableCell>
                        <TableCell>{org.permission}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
      <RightNavbar />
      <Toaster />
    </div>
  );
}
